import React, { useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../../../App.css';
import { apiUrlPrefix } from '../../../config';
import useStore from '../state-files/zustondStoreStates';
import Cookies from 'js-cookie';
import fetchCommunities from '../helper-methods/fetchCommunities';
import GetUsers from '../Components/getUsers';
import getAllUsers from '../helper-methods/getAllUsers';

const CreateCommunityWidget = ({ closeMenu, isBold }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setName] = useState();
  const { setCommunities } = useStore(); // Use zustand store
  //const [allUserData, setAllUserData] = useState();
  const queryClient = useQueryClient();

  // Use TanStack Query to manage the user list
  const { data: usersData } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => getAllUsers(setName),
    enabled: open, // Only fetch when the dialog is opened
  });

  // --- MUTATION LOGIC ---
  const createCommunity = async ({ title, users }) => {
    // Since you are using 'multiple' in Autocomplete, 
    // 'users' is already the array you need!
    const apiUrl = apiUrlPrefix + 'Communities/createCommunity';

    // 1. Map the names (from Formik) back to IDs (from usersData)
    const userIds = users.map(selectedName => {
      // If 'All' is selected, handle it based on your backend logic 
      // (usually you'd send a special ID or the string 'All')
      if (selectedName === 'All') return 'All';
      if (selectedName === currentUser) return localStorage.getItem('userId');

      // Find the original user object that matches the selected name
      const foundUser = usersData.find(u => u.name === selectedName);

      // Return the ID if found, otherwise return the name as a fallback
      return foundUser ? foundUser.id : selectedName;
    });

    // 2. Extra safety check for "All"
    let finalUsers = userIds;
    if (finalUsers.length > 1 && finalUsers.includes("All")) {
      finalUsers = finalUsers.filter(id => id !== "All");
      formik.setFieldValue("users", finalUsers); // Update UI state
    }

    var currentUserId = localStorage.getItem('userId'); //Makes the current user an admin
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Name: title,
        Users: finalUsers, // Send the array directly
        Admins: [currentUserId]
      }),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid credentials or permissions.');
      }
      throw new Error('Network response was not ok');
    }
    return response.json();
  };

  const createCommunityMutation = useMutation({
    mutationFn: createCommunity,
    onSuccess: (data) => {
      if (data) {
        fetchCommunities(setCommunities); //Use this to fetch all communities
        handleClose();
      } else {
        throw new Error('Unexpected response structure');
      }
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // --- DIALOG CONTROL ---
  const handleClickOpen = (e) => {
    e.stopPropagation(); // Prevent Menu closing until we want it to
    setOpen(true);
  };

  const handleClose = () => {
    setError(null);
    setOpen(false);
    if (closeMenu) closeMenu();
  };

  const formik = useFormik({
    initialValues: {
      title: '',
      users: ['All'],
    },
    validationSchema: Yup.object({
      title: Yup.string().required('Title is required'),
      users: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one user is required')
        .required('At least one user is required'),
    }),
    onSubmit: (values) => {
      setError(null);
      createCommunityMutation.mutate(values);
    },
  });

  return (
    <div>
      <Button
        id="createCommunityButton"
        variant="contained"
        color="none"
        onClick={handleClickOpen}
        style={{
          fontWeight: isBold ? 'bold' : 'none',
          boxShadow: 'none'
        }}
      >
        Create Community
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Create Community</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Community name"
              type="text"
              fullWidth
              variant="outlined"
              name="title"
              value={formik.values.title}
              onKeyDown={(e) => {
                // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
                e.stopPropagation();
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />

            <GetUsers formik={formik} setName={setName} currentUser={currentUser} />
            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: '10px' }}>
                {error}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={createCommunityMutation.isLoading}>
              {createCommunityMutation.isLoading ? 'Creating Community...' : 'Create Community'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default CreateCommunityWidget;